import { computed, signal } from '@angular/core';
import { DriveItemDetails } from '@common/models/drive.model';
import { MimeCategory } from '../enums/mime-type.enum';

export interface TeamItem {
  Id: string;
  Name: string;
  ItemType: TeamItemType;
  MimeCategory: MimeCategory;
}

export enum TeamItemType {
  Team = 'Team',
  Channel = 'Channel',
  FilesRoot = 'FilesRoot',
  PostsRoot = 'PostsRoot',
  File = 'File',
  Folder = 'Folder',
  Root = 'Root' // only for manual create root node
}

export interface ChannelItem extends TeamItem {
  ChannelId: string;
}

export interface TeamOdataPost {
  Id: number;
  FromName: string;
  CreatedDate: string;
  Subject: string; // html content
  Content: string; // html content
  ExistReplies: boolean;
}

// Graph sends Teams emoji as an empty <emoji alt="😊"> element: browsers don't render its alt and DOMPurify drops the unknown tag
const TEAMS_EMOJI_REGEX = /<emoji\b[^>]*\balt="([^"]*)"[^>]*>\s*<\/emoji>/gi;
const HTML_TAG_OR_NBSP_REGEX = /<[^>]*>|&nbsp;/gi;
const IMG_TAG_REGEX = /<img\b/i;

// markup without text like <p></p> or <div><br></div> is a truthy string, so the template would still render an empty block
const emptyHtmlToNull = (html: string): string =>
  html && (IMG_TAG_REGEX.test(html) || html.replace(HTML_TAG_OR_NBSP_REGEX, '').trim()) ? html : null;

export class TeamPost {
  public readonly id: number;
  public readonly userName: string;
  public readonly date: Date;
  public readonly subject: string;
  public readonly content: string;
  public readonly hasReplies: boolean;

  public readonly contentState = signal<'expanded' | 'collapsed'>('collapsed');
  public readonly showReplies = signal<boolean>(false);
  public readonly replies = signal<TeamPost[]>([]);
  public readonly repliesLoading = signal<boolean>(false);

  public readonly repliesSet = computed<boolean>(() => this.replies()?.length > 0);

  constructor(post: TeamOdataPost) {
    this.id = post.Id;
    this.userName = post.FromName;
    this.date = new Date(post.CreatedDate);
    this.subject = emptyHtmlToNull(post.Subject);
    this.content = emptyHtmlToNull(post.Content?.replace(TEAMS_EMOJI_REGEX, (_, emoji: string) => emoji));
    this.hasReplies = post.ExistReplies;
  }
}

export interface TeamFile extends TeamItem {
  ChannelId: number;
  DbId: string;
  Size: number;
  Type: TeamFileItemType;
}

export enum TeamFileItemType {
  Folder,
  File
}

export type ItemTeamItemDetails = { drive: TeamFile; details: DriveItemDetails };
